/**
 * WelcomeChecklist — the Console first-login onboarding experience (F12).
 *
 * A brief, value-first welcome plus a 3-5 item getting-started checklist tied to
 * the platform's activation milestone: submit your first job and see its Adam
 * and Eve decision. Mounted in ConsoleShell around the content area so it
 * precedes every screen; it is zero-prop and self-fetches its gating data (like
 * the tabs).
 *
 * Gating (FR2): the onboarding UI shows iff the customer's onboarded_at IS NULL.
 * A returning/onboarded customer (onboarded_at set) sees the normal Console with
 * no first-run UI. Gating comes from the existing getProfile() (onboarded_at)
 * plus listJobs(1) for the zero-jobs / decision signals — no bespoke endpoint.
 *
 * Auto-advance (FR4): checklist items derive from real signals — a project
 * present (listProjects), a job present (jobs.length > 0), a decision returned
 * (a job row carries a decision), and, on the developer track, a live API key
 * (listApiKeys). The guided track's closing "Check usage and billing" step is
 * the one signal with no server side to read: opening the Billing screen IS the
 * step, so BillingTab records its own mount (engine/visitedScreens.js).
 * Reaching the milestone (first job -> first decision) or an explicit skip calls
 * completeOnboarding(), which durably stamps onboarded_at (FR5/FR8), so a
 * completed/skipped customer never sees the first-run UI again on any device. A
 * lighter "Dismiss for now" is a transient, per-browser localStorage flag.
 *
 * Persona (FR7): a GUI variant (submit via the form) and a developer variant
 * (create an API key + submit via the API) reuse the F08 GUI/Developer tracks;
 * the user can switch.
 *
 * Colour semantics are strict (.impeccable.md / FR9): a MET item uses the `ok`
 * token; an UNMET item stays neutral `fg3` (NEVER danger/red for "not done");
 * accent is interactive only. No em dashes in user-facing strings.
 */

import { useEffect, useRef, useState } from "react";
import Panel from "./Panel";
import usePolledFetch from "../hooks/usePolledFetch";
import { requestScreen } from "../engine/consoleNav";
import { getProfile, listJobs, listProjects, listApiKeys, completeOnboarding } from "../engine/api";
import { hasVisited, subscribeVisited } from "../engine/visitedScreens";
import { DEMO_JOB } from "../content/guide";

// Transient, per-browser "dismiss for now" flag (FR8). Durable completion lives
// server-side in users.onboarded_at, not here.
const DISMISS_KEY = "omega_onboarding_dismissed";

// Reuse the F08 "Try this example" spec as the one-shot Submit prefill (the same
// DeepLink envelope the Guide uses). meta.demo lights the passive "this is a
// real, billable job" reminder on the Submit screen (the F09 seam), so a
// prefilled first job is never presented as free.
const EXAMPLE_PREFILL = { fields: DEMO_JOB, meta: { demo: true } };

function readDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* storage unavailable — non-fatal (dismiss then falls back to session-only) */
  }
}

/**
 * Build the ordered checklist for a persona from the live signals. The shared
 * spine is account -> create a project -> submit -> see the decision; the persona
 * only changes how the first job is submitted and the final "explore next" step.
 * Every list stays within 3-5 items (FR4).
 *
 * Creating a project comes first (FR3): a brand-new customer has no project, and
 * a job cannot be submitted without one, so the checklist guides them to create
 * one before Submit rather than auto-provisioning a default.
 *
 * The signals arrive as a named object rather than positionally: there are five
 * of them, they are all booleans, and transposing two would be silent.
 */
function buildItems(persona, { hasProject, hasJob, hasDecision, hasApiKey, billingVisited }) {
  const account = { id: "account", label: "Account ready", met: true };
  const createProject = {
    id: "create-project",
    label: "Create your first project",
    met: hasProject,
    actionLabel: "Create a project",
    onAction: () => requestScreen("projects"),
  };
  const decision = {
    id: "decision",
    label: "See your Adam and Eve decision",
    met: hasDecision,
    actionLabel: "Open Jobs",
    onAction: () => requestScreen("jobs"),
  };

  if (persona === "developer") {
    return [
      account,
      createProject,
      {
        id: "api-key",
        label: "Create an API key",
        met: hasApiKey,
        actionLabel: "Open Account",
        onAction: () => requestScreen("account"),
      },
      {
        id: "submit",
        label: "Submit your first job via the API",
        met: hasJob,
        actionLabel: "Open the developer guide",
        onAction: () => requestScreen("guide"),
      },
      decision,
    ];
  }

  return [
    account,
    createProject,
    {
      id: "submit",
      label: "Submit your first job",
      met: hasJob,
      actionLabel: "Go to Submit",
      // A projectless customer can't submit (FR3). Route them to create a project
      // first rather than to a Submit screen whose one-shot prefill they would
      // immediately lose when the no-project prompt sends them to Projects.
      onAction: hasProject
        ? () => requestScreen("submit", EXAMPLE_PREFILL)
        : () => requestScreen("projects"),
    },
    decision,
    {
      id: "billing",
      label: "Check usage and billing",
      met: billingVisited,
      actionLabel: "Open Billing",
      onAction: () => requestScreen("billing"),
    },
  ];
}

function ChecklistItem({ item }) {
  const { id, label, met, actionLabel, onAction } = item;
  return (
    <li data-testid={`onboarding-item-${id}`} className="flex items-center gap-3 py-1.5">
      <span
        data-testid={`onboarding-marker-${id}`}
        aria-hidden="true"
        className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ${
          met ? "bg-ok-tint text-ok" : "bg-surface2 text-fg3"
        }`}
      >
        {met ? "✓" : ""}
      </span>
      <span className={`min-w-0 flex-1 text-sm ${met ? "text-fg" : "text-fg2"}`}>{label}</span>
      {met ? (
        <span className="text-xs text-ok">Done</span>
      ) : (
        onAction && (
          <button
            data-testid={`onboarding-action-${id}`}
            onClick={onAction}
            className="shrink-0 text-sm text-accent hover:text-accent-strong bg-accent-tint rounded-none px-3 py-1 border border-accent/40 cursor-pointer"
          >
            {actionLabel}
          </button>
        )
      )}
    </li>
  );
}

export default function WelcomeChecklist() {
  // Profile drives the FR2 gate (onboarded_at). Fetched once on mount — the
  // stamp only changes via completeOnboarding(), which we track locally.
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileErr, setProfileErr] = useState(false);
  const [onboardedAt, setOnboardedAt] = useState(null);

  // Session state. `hidden` starts from the transient localStorage flag (FR8) so
  // a "dismiss for now" survives a screen switch within the session.
  const [hidden, setHidden] = useState(() => readDismissed());
  const [completed, setCompleted] = useState(false);
  // Explicit skip. Distinct from `hidden`: skipping settles onboarding for good
  // (it calls completeOnboarding), whereas a dismiss only hides this session.
  const [skipped, setSkipped] = useState(false);
  const [persona, setPersona] = useState("gui");

  // Welcome inline-dialog (FR3): auto-opens on first render; Escape/skip closes
  // and returns focus to the trigger (the F08 DemoLaunch pattern).
  const [welcomeOpen, setWelcomeOpen] = useState(true);
  const welcomeTriggerRef = useRef(null);
  const welcomePrimaryRef = useRef(null);

  const completedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    getProfile()
      .then((p) => {
        if (alive) setOnboardedAt(p?.onboarded_at ?? null);
      })
      .catch(() => {
        // Cannot determine onboarding state (e.g. 503/404) -> fail closed and
        // show nothing rather than nag a possibly-returning customer.
        if (alive) setProfileErr(true);
      })
      .finally(() => {
        if (alive) setProfileLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Is there any outcome left for this component to produce? It has exactly two
  // jobs: render the checklist, and detect the activation milestone. Once
  // onboarding is settled neither is possible, and every poll below is noise.
  //
  // `hidden` is deliberately NOT part of this. "Dismiss for now" only hides the
  // surface for the session; the milestone must still be able to stamp
  // onboarded_at, which L02's activation-nudge email reads. An explicit skip is
  // the opposite: it already called completeOnboarding, so nothing is left.
  // (A skip does not set `onboardedAt` locally, which is why `skipped` has to be
  // its own flag rather than being inferred from the profile.)
  const onboardingLive = profileLoaded && !profileErr && !onboardedAt && !completed && !skipped;

  // Zero-jobs / decision signal. listJobs(1) is enough: the most recent row
  // tells us both "a job exists" and "it has a decision". Stop polling once a
  // decision is present (the milestone is settled).
  const { data: jobsData } = usePolledFetch(() => listJobs(1), {
    intervalMs: 6000,
    enabled: onboardingLive,
    stopWhen: (d) => (d?.jobs ?? []).some((j) => j?.decision != null && j.decision !== ""),
  });
  const jobs = jobsData?.jobs ?? [];
  const hasJob = jobs.length > 0;
  const hasDecision = jobs.some((j) => j?.decision != null && j.decision !== "");
  const milestone = hasJob && hasDecision;

  // Project signal (FR3): a brand-new customer has no project and cannot submit
  // until they create one, so the checklist gates Submit behind it. Poll on the
  // same cadence as the jobs signal so the create-project item auto-advances
  // (FR4) when the user creates one mid-session; stop once a project exists (the
  // signal is settled). usePolledFetch keeps data null on error, so the `?? []`
  // below fails safe to "no projects" without crashing.
  const { data: projectsData } = usePolledFetch(() => listProjects(), {
    intervalMs: 6000,
    enabled: onboardingLive,
    stopWhen: (d) => (d?.projects ?? []).length > 0,
  });
  const projects = projectsData?.projects ?? [];
  const hasProject = projects.length > 0;

  // API-key signal (OMEGA-815) for the developer track's "Create an API key"
  // step, which was a hardcoded `met: false` and so could never tick. The
  // signal is the same list the Account screen renders (GET
  // /tenants/me/api-keys). Three things differ from the two signals above, and
  // each is deliberate:
  //   * The predicate is "some key is LIVE", never `keys.length`. A revoked key
  //     stays in the list (F07 FR4 keeps the row visible for audit), so a
  //     length check would tick on the first key ever created and never untick.
  //   * NO `stopWhen`. A project appearing and a decision landing are terminal
  //     — once true they never go back — but a key can be revoked. Settling the
  //     poll on the first live key would strand the item on met when the user
  //     revokes their last key on Account, since this component stays mounted
  //     across screen switches and would never fetch again.
  //   * `enabled` adds `!hidden` to the shared gate, which the two signals
  //     above deliberately omit. This one feeds no effect, only the rendered
  //     item, so while the surface is dismissed there is nothing for it to do.
  const { data: keysData } = usePolledFetch(() => listApiKeys(), {
    intervalMs: 6000,
    enabled: onboardingLive && !hidden,
  });
  const hasApiKey = (keysData?.keys ?? []).some((k) => !k?.revoked_at);

  // Billing signal (OMEGA-815) for the guided track's closing "Check usage and
  // billing" step, the other item that was a hardcoded `met: false`. Alone
  // among the five it has no endpoint to poll: it is an action the customer
  // takes, not a state the backend holds, so BillingTab records its own mount
  // and this subscribes to that. Seeded from hasVisited() so a customer who
  // opened Billing before this component mounted is not missed.
  const [billingVisited, setBillingVisited] = useState(() => hasVisited("billing"));
  useEffect(() => subscribeVisited(() => setBillingVisited(hasVisited("billing"))), []);

  // Move focus into the welcome dialog on open (F08 DemoLaunch idiom). Depends on
  // profileLoaded/completed too: the dialog is not in the DOM until the profile
  // gate resolves, so the initial auto-open would otherwise focus a null ref and
  // never re-run (welcomeOpen never transitions). The `?.focus()` is null-safe
  // for the gating branches that render no dialog.
  useEffect(() => {
    if (welcomeOpen && profileLoaded && !completed) welcomePrimaryRef.current?.focus();
  }, [welcomeOpen, profileLoaded, completed]);

  // Activation milestone (FR5): first job -> first decision durably completes
  // onboarding. Wait for the profile gate first (so we never stamp for a user
  // whose onboarded state is still unknown or already set), then call the
  // idempotent endpoint once per mount; a failure resets the guard for a retry.
  useEffect(() => {
    if (!profileLoaded || profileErr || onboardedAt) return;
    if (!milestone || completedRef.current) return;
    completedRef.current = true;
    completeOnboarding()
      .then(() => setCompleted(true))
      .catch(() => {
        completedRef.current = false;
      });
  }, [profileLoaded, profileErr, milestone, onboardedAt]);

  // ── Gating (render nothing when there is no first-run experience to show) ──
  if (!profileLoaded || profileErr) return null;
  if (onboardedAt) return null; // returning/onboarded customer (FR2)
  if (hidden) return null; // dismissed for now (FR8) or skipped this session

  const closeWelcome = () => {
    setWelcomeOpen(false);
    welcomeTriggerRef.current?.focus();
  };

  const dismissForNow = () => {
    writeDismissed();
    setHidden(true);
  };

  const skipOnboarding = () => {
    // Explicit skip completes onboarding durably (FR5/FR8). Hide regardless of
    // the network result so the surface goes away immediately this session.
    completedRef.current = true;
    completeOnboarding().catch(() => {});
    setSkipped(true);
    setHidden(true);
  };

  if (completed) {
    return (
      <div data-testid="onboarding" className="px-6 pt-5">
        <Panel title="You are all set" sub="First job submitted and routed">
          <div className="flex items-center gap-3 py-1">
            <span
              aria-hidden="true"
              className="shrink-0 w-6 h-6 rounded-full bg-ok-tint text-ok flex items-center justify-center text-sm font-semibold"
            >
              {"✓"}
            </span>
            <p className="flex-1 text-sm text-fg">
              You submitted a job and saw its Adam and Eve decision. That is the core of Omega, an
              explainable, priced routing decision on every job. Explore Jobs, Billing and the Guide
              whenever you need them.
            </p>
            <button
              data-testid="onboarding-complete"
              onClick={() => setHidden(true)}
              className="shrink-0 text-sm text-fg2 hover:text-fg rounded-none px-3 py-1 border border-line cursor-pointer"
            >
              Dismiss
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  const items = buildItems(persona, { hasProject, hasJob, hasDecision, hasApiKey, billingVisited });

  return (
    <div data-testid="onboarding" className="px-6 pt-5">
      <Panel title="Getting started" sub="Submit your first job and see its Adam and Eve decision">
        <div className="space-y-4">
          {/* Welcome step (FR3): value-first, outcome-first, single step. */}
          {welcomeOpen ? (
            <div
              role="dialog"
              aria-label="Welcome to Omega AI HPC"
              data-testid="onboarding-welcome"
              onKeyDown={(e) => {
                if (e.key === "Escape") closeWelcome();
              }}
              className="rounded-none border border-line bg-surface2 px-4 py-3 space-y-3"
            >
              <p className="text-sm text-fg">
                Omega AI HPC routes your compute jobs to the best venue and prices them for you.
                Every job comes back with an explainable Adam and Eve decision, so you can see
                exactly why it was approved, countered, or vetoed. The fastest way to see it is to
                submit one job.
              </p>
              <div className="flex items-center gap-2">
                <button
                  ref={welcomePrimaryRef}
                  data-testid="onboarding-welcome-submit"
                  onClick={() => {
                    // A projectless customer can't submit (FR3): route them to
                    // create a project first, otherwise the one-shot prefill is
                    // consumed on Submit mount and then lost the moment the
                    // no-project prompt sends them to Projects.
                    if (hasProject) requestScreen("submit", EXAMPLE_PREFILL);
                    else requestScreen("projects");
                    closeWelcome();
                  }}
                  className="text-sm text-accent hover:text-accent-strong bg-accent-tint rounded-none px-3 py-1 border border-accent/40 cursor-pointer"
                >
                  Submit your first job
                </button>
                <button
                  data-testid="onboarding-welcome-skip"
                  onClick={closeWelcome}
                  className="text-sm text-fg2 hover:text-fg rounded-none px-3 py-1 border border-line cursor-pointer"
                >
                  Not now
                </button>
              </div>
            </div>
          ) : (
            <button
              ref={welcomeTriggerRef}
              data-testid="onboarding-welcome-trigger"
              onClick={() => setWelcomeOpen(true)}
              className="text-sm text-accent hover:text-accent-strong cursor-pointer"
            >
              What is Omega?
            </button>
          )}

          {/* Persona branch (FR7): reuse the F08 GUI / Developer tracks. */}
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-fg3">Track</span>
            {[
              ["gui", "Guided"],
              ["developer", "Developer"],
            ].map(([id, label]) => (
              <button
                key={id}
                data-testid={`onboarding-persona-${id}`}
                onClick={() => setPersona(id)}
                className={`text-sm rounded-none px-3 py-1 cursor-pointer transition-colors duration-150 ${
                  persona === id
                    ? "bg-accent-tint text-accent font-medium border border-accent/40"
                    : "text-fg2 hover:text-fg border border-line"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Checklist (FR4/FR5). */}
          <ul data-testid="onboarding-checklist" className="divide-y divide-line/60">
            {items.map((item) => (
              <ChecklistItem key={item.id} item={item} />
            ))}
          </ul>

          {/* Exits (FR4/FR8): a durable skip and a transient dismiss. */}
          <div className="flex items-center gap-4 pt-1 border-t border-line/60">
            <button
              data-testid="onboarding-skip"
              onClick={skipOnboarding}
              className="text-sm text-fg2 hover:text-fg cursor-pointer"
            >
              Skip onboarding
            </button>
            <button
              data-testid="onboarding-dismiss"
              onClick={dismissForNow}
              className="text-sm text-fg3 hover:text-fg2 cursor-pointer"
            >
              Dismiss for now
            </button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
